import { ArrowRight } from 'lucide-react'
/**
 * "Proceed to Full Screen" confirm (TT-BILL-200). Upgrades a COMPLETED
 * pre-screening application to full screening: runs the credit & rental-history
 * check (TenCheck), replaces the pre-screen report with a full report, and
 * deducts the DIFFERENCE to the full-screen price (the pre-screen 10 already
 * paid → only the full-only add-ons are charged, ~25 credits).
 *
 * Posts to /resources/screening-upgrade. The combined page upgrades EVERY
 * pre-screening member at once (client 2026-08-27) by passing `targets`, so
 * the endpoint takes a comma list; the individual page passes none and
 * upgrades its own applicant. Result handling uses the module's ref-callback
 * marker idiom (no useEffect), like RegenerateDialog.
 */
import { useRef } from 'react'
import { useFetcher, useRevalidator } from 'react-router'
import { toast } from 'sonner'
import type { ApplicationSummary } from '#app/utils/application-detail-v2/types'
import { DetailDialog, PillButton } from './dialog-bits'

type UpgradeResult =
	| { success: true; applicationId: string; message?: string }
	| { error: string }

export function ProceedToFullScreenDialog({
	open,
	onOpenChange,
	application,
	targets,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	/** Members to upgrade; omitted = just this page's applicant. */
	targets?: Array<{ id: string; name: string }>
}) {
	const upgrades =
		targets && targets.length > 0
			? targets
			: [{ id: application.id, name: application.applicantName }]
	const fetcher = useFetcher<UpgradeResult>()
	const revalidator = useRevalidator()
	const busy = fetcher.state !== 'idle'

	const lastHandled = useRef<UpgradeResult | null>(null)
	const result = fetcher.state === 'idle' ? fetcher.data : undefined
	const marker =
		result && lastHandled.current !== result ? (
			<span
				hidden
				ref={() => {
					if (lastHandled.current === result) return
					lastHandled.current = result
					if ('success' in result && result.success) {
						toast.info(
							result.message ||
								`Full screening started for ${upgrades
									.map((target) => target.name)
									.join(', ')}. Processing…`,
						)
						onOpenChange(false)
						void revalidator.revalidate()
					} else if ('error' in result) {
						toast.error(result.error)
					}
				}}
			/>
		) : null

	return (
		<>
			{marker}
			<DetailDialog
				open={open}
				onOpenChange={onOpenChange}
				title="Proceed to Full Screening"
				description={
					upgrades.length > 1
						? `${upgrades
								.map((target) => target.name)
								.join(
									', ',
								)} move back to Processing while full screening runs — nothing more is needed from them. The combined report generates once every report is in.`
						: 'The application moves back to Processing while it runs — nothing more is ' +
							'needed from the applicant.'
				}
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			>
				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone="dark"
						disabled={busy}
						onClick={() => {
							void fetcher.submit(
								{
									applicationIds: upgrades.map((target) => target.id).join(','),
								},
								{ method: 'post', action: '/resources/screening-upgrade' },
							)
						}}
					>
						{busy
							? 'Starting…'
							: upgrades.length > 1
								? `Run Full Screen On ${upgrades.length} Applicants`
								: 'Proceed to Full Screen'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
